/**
 * Case-profile helpers for Midstream Referrals "Withhold Client Contact".
 *
 * The BFF has already stripped a withheld client's last name, phone and email
 * from the payload. These helpers only decide how the page presents that.
 */
import { CASE_PURCHASE_STATUS } from '@/constants'
import { Case } from './types'

/**
 * Whether the client's contact info is withheld from this viewer, who is then
 * on the buying side. The seller side is also told its match is withheld (it
 * owes the share) but keeps the full contact, hence `is_owner`. Data-driven:
 * the rollout flag only gates the chip and explainer.
 */
export const isBuyerContactWithheld = (caseData: Case | null): boolean =>
  Boolean(caseData?.is_client_contact_withheld && !caseData.is_owner)

/**
 * The seller's owed share, as both the contact card's Share Contact Info
 * button and the status line's withheld cell open it (ENG-2066). Only the
 * seller side is sent `share_case_purchase_id`, and `is_owner` keeps a buyer
 * -- who is also told its match is withheld -- from ever being offered the
 * share.
 *
 * Deliberately not flag-gated: masking follows the data, so the way out of
 * it must too. A gated share would strand every withheld match when the
 * flag is off.
 */
export const getSellerShareState = (caseData: Case | null | undefined) => {
  const isSeller = Boolean(caseData?.is_owner)
  const sharePurchaseId = isSeller
    ? caseData?.share_case_purchase_id ?? null
    : null
  const sharedNegotiation = caseData?.sidebar?.negotiations?.find(
    negotiation => negotiation.id === sharePurchaseId
  )
  return {
    isSeller,
    sharePurchaseId,
    awaitingShare: Boolean(
      sharePurchaseId && caseData?.is_client_contact_withheld
    ),
    buyerFirmName: sharedNegotiation?.counterpart?.firm?.name ?? null,
    // The buyer can sign before the share; that purchase is already charged,
    // so the confirmation must not promise an escrow period.
    purchaseSigned: sharedNegotiation?.status === CASE_PURCHASE_STATUS.Signed,
  }
}

/**
 * The referring firm, as the buyer's sidebar knows it: the header counterpart
 * once the purchase has settled, else the buyer's own negotiation's. Null
 * when neither names one, and the copy then says "The referring attorney".
 */
export const getReferringFirmName = (caseData: Case): string | null =>
  caseData.sidebar?.header?.counterpart?.firm?.name ??
  caseData.sidebar?.negotiations?.[0]?.counterpart?.firm?.name ??
  null

/**
 * The client name the BFF prefixed to this viewer's title as "(Name) ", when
 * the chat title must drop it; undefined when the title is kept as it is.
 *
 * - Seller side of a represented case: the prefix is the client's full name.
 *   Only the seller side is ever sent `client_currently_represented`, and
 *   its client_contact_info carries that same first lead.
 * - Buyer whose contact is withheld: the prefix is the masked "Maria G.",
 *   built from the same masked first lead the sidebar gets --
 *   special_agreements while Matched, client_contact_info once Signed.
 */
const getTitleClientPrefixName = (caseData: Case): string | undefined => {
  const { sidebar } = caseData
  if (caseData.client_currently_represented) {
    return sidebar?.client_contact_info?.leads?.[0]?.name?.trim()
  }
  if (isBuyerContactWithheld(caseData)) {
    return (
      sidebar?.special_agreements?.leads?.[0]?.name?.trim() ||
      sidebar?.client_contact_info?.leads?.[0]?.name?.trim()
    )
  }
  return undefined
}

/**
 * The title to store on a Stream case channel the viewer creates.
 *
 * Every member of a case channel reads its stored `caseTitle`, and the
 * seller's own title carries the client's full name -- the BFF builds it as
 * "(Maria Garcia) Case name". For a case whose client is currently
 * represented the buyer must not see that name before the share, so the
 * seller writes the title without the client prefix.
 *
 * A withheld buyer's title is already masked ("(Maria G.) Case name"), but a
 * channel keeps the title of whoever opened it, so a buyer who opens the chat
 * first would pin the masked name on both sides for good, even after the
 * share. The buyer drops its prefix too, so both sides store the same title.
 *
 * Every other case keeps today's title.
 */
export const getCaseChatTitle = (caseData: Case): string => {
  const title = caseData.name || ''

  const clientName = getTitleClientPrefixName(caseData)
  // No client, no prefix: the BFF adds one only for a named first lead.
  if (!clientName) return title

  const prefix = `(${clientName}) `
  if (title.startsWith(prefix)) return title.slice(prefix.length)

  // The prefix is the BFF's, built from that same lead, so this is not
  // expected. Fail closed and drop any leading parenthesised name.
  return title.replace(/^\([^)]*\)\s+/, '')
}
